/**
 * Componente de Chat
 * Basado en ChatScreen.cs
 */

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChatMessage } from '../../types';
import { ChatMessage as ChatMessageComponent } from './ChatMessage';
import { useChatFeatures } from '../../hooks/useChatFeatures';
import './Chat.css';

export interface ChatTab {
  id: string;
  /** Fecha corta: "Hoy 10:30", "Ayer", "lun 1 sep". */
  label: string;
  /** Primer mensaje del estudiante, para el tooltip. */
  preview: string;
}

interface ChatProps {
  messages: ChatMessage[];
  onSendMessage?: (text: string) => void;
  inline?: boolean;
  /**
   * Chat EMBEBIDO en una columna fija, siempre visible, sin botón para abrirlo
   * ni portal flotante. Lo usa el layout «Espace de travail» (rediseño opción
   * 1a, WorkspaceLayout.tsx): contenido y chat en dos columnas permanentes.
   * El cuerpo del panel es el mismo que el del modo `inline`.
   */
  embedded?: boolean;
  /** Elemento extra en la fila del campo de texto, antes de «enviar» (p. ej. el micrófono). */
  inputAccessory?: React.ReactNode;
  /** Texto de ayuda del campo; por defecto el de siempre (con la pista de Enter). */
  placeholder?: string;
  onVisibilityChange?: (isVisible: boolean) => void;
  /**
   * Controlar visibilidad desde fuera (prop controlada)
   */
  isVisibleControlled?: boolean;
  /**
   * Modo actual de práctica (para habilitar/deshabilitar funcionalidades)
   */
  mode?: string;
  /**
   * Habilitar copiar/pegar en el input
   * Por defecto: true
   */
  enableCopyPaste?: boolean;
  /**
   * Habilitar reproducir audio al hacer click en burbujas
   * Por defecto: true
   */
  enableAudioReplay?: boolean;
  /**
   * Adjuntar un documento (modo profesor / aide-prof). Recibe el File, lo procesa
   * y devuelve si se adjuntó bien. Si no se pasa, no se muestra el botón de adjuntar.
   */
  onAttachDocument?: (file: File) => Promise<{ ok: boolean; error?: string }>;
  /**
   * Pestañas de conversaciones recientes (acuerdo Santiago 01/09/2026): las
   * últimas 3 conversaciones del mismo modo (único criterio, sin vigencia;
   * Carlos 07/09/2026), en la parte superior del cuadro de chat en lugar de
   * los chips de sugerencias.
   */
  recentConversations?: ChatTab[];
  /** internal_interaction_id de la conversación que se está escribiendo ahora. */
  activeConversationId?: string;
  onSelectConversation?: (id: string) => void;
  onNewConversation?: () => void;
}

export function Chat({ 
  messages, 
  onSendMessage, 
  inline = false, 
  embedded = false,
  inputAccessory,
  placeholder,
  onVisibilityChange,
  isVisibleControlled,
  mode,
  enableCopyPaste = true,
  enableAudioReplay = true,
  onAttachDocument,
  recentConversations,
  activeConversationId,
  onSelectConversation,
  onNewConversation,
}: ChatProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [inputText, setInputText] = useState('');
  const isSendingRef = useRef(false); // Guard para evitar envíos duplicados

  // Adjuntar documento (modo profesor / aide-prof)
  const [attachedDocName, setAttachedDocName] = useState<string | null>(null);
  const [docStatus, setDocStatus] = useState<'idle' | 'loading' | 'error'>('idle');
  const [docError, setDocError] = useState('');
  const isAideProf = mode === 'actividades-docente';
  const showAttach = isAideProf && !!onAttachDocument;

  // Textos de la interfaz del chat. En modo profesor (aide-prof) toda la UI va en
  // francés (el profesor es personal de la AF); en modo estudiante se mantienen en
  // español, que es el idioma de apoyo del estudiante.
  const t = {
    empty: isAideProf
      ? "Aucun message pour l'instant. Écris quelque chose pour commencer."
      : 'No hay mensajes aún. Escribe algo para comenzar.',
    placeholder: isAideProf
      ? 'Écris un message… (Entrée pour envoyer, Maj+Entrée pour un saut de ligne)'
      : 'Escribe un mensaje... (Enter para enviar, Shift+Enter para salto de línea)',
    send: isAideProf ? 'Envoyer le message' : 'Enviar mensaje',
    showChat: isAideProf ? 'Afficher le chat' : 'Mostrar chat',
    hideChat: isAideProf ? 'Masquer le chat' : 'Ocultar chat',
    attach: isAideProf ? 'Joindre un document (PDF ou texte)' : 'Adjuntar documento (PDF o texto)',
    docProcessing: isAideProf ? 'Traitement du document…' : 'Procesando documento…',
    docAttached: isAideProf
      ? 'joint — dis à Galia ce que tu veux en faire'
      : 'adjuntado — dile a Galia qué hacer con él',
    tabsLabel: isAideProf ? 'Conversations récentes' : 'Conversaciones recientes',
    tabCurrent: isAideProf ? 'En cours' : 'Actual',
    tabNew: isAideProf ? 'Nouvelle' : 'Nueva',
    tabNewTitle: isAideProf ? 'Commencer une nouvelle conversation' : 'Empezar una conversación nueva',
  };
  const suggestions = isAideProf
    ? ["Idée d'activité pour ma classe", 'Exercice de grammaire avec Galia', 'Activité à faire à la maison', 'Comment évaluer avec Galia ?']
    : ['Bonjour !', 'Parle-moi de toi', 'Comment ça va ?', 'La météo'];

  // Pestañas de conversaciones recientes. La conversación en curso siempre es la
  // primera ("Actual"); luego las recientes (sin la actual); al final "+ Nueva".
  // Se muestran cuando la app las provee, aunque no haya recientes: así el
  // estudiante siempre ve dónde está y cómo empezar de cero.
  const renderTabs = () => {
    if (!recentConversations) return null;
    const others = recentConversations.filter((c) => c.id !== activeConversationId);
    return (
      <div className="chat-tabs" role="tablist" aria-label={t.tabsLabel}>
        <button
          type="button"
          role="tab"
          aria-selected={true}
          className="chat-tab chat-tab--active"
          title={t.tabCurrent}
        >
          {t.tabCurrent}
        </button>
        {others.map((c) => (
          <button
            key={c.id}
            type="button"
            role="tab"
            aria-selected={false}
            className="chat-tab"
            title={c.preview || c.label}
            onClick={() => onSelectConversation?.(c.id)}
          >
            {c.label}
          </button>
        ))}
        {onNewConversation && (
          <button
            type="button"
            className="chat-tab chat-tab--new"
            title={t.tabNewTitle}
            aria-label={t.tabNewTitle}
            onClick={() => onNewConversation()}
          >
            + {t.tabNew}
          </button>
        )}
      </div>
    );
  };

  const handleAttachClick = () => fileInputRef.current?.click();
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // permitir re-seleccionar el mismo archivo
    if (!file || !onAttachDocument) return;
    setDocStatus('loading');
    setDocError('');
    try {
      const res = await onAttachDocument(file);
      if (res.ok) {
        setAttachedDocName(file.name);
        setDocStatus('idle');
      } else {
        setDocStatus('error');
        setDocError(res.error || 'No se pudo adjuntar el documento.');
      }
    } catch (err: any) {
      setDocStatus('error');
      setDocError(err?.message || 'No se pudo adjuntar el documento.');
    }
  };
  
  // Si se proporciona isVisibleControlled, usar ese valor en lugar del estado interno
  // Embebido = siempre visible (la columna del chat no se cierra).
  const actualIsVisible = embedded
    ? true
    : isVisibleControlled !== undefined ? isVisibleControlled : isVisible;
  
  // Hook reutilizable para funcionalidades del chat
  const { handlePaste, handleMessageClick, handleCopyMessage } = useChatFeatures({
    enableCopyPaste,
    enableAudioReplay,
    mode
  });

  // Sincronizar estado interno cuando cambia isVisibleControlled
  useEffect(() => {
    if (isVisibleControlled !== undefined) {
      setIsVisible(isVisibleControlled);
    }
  }, [isVisibleControlled]);

  // Notificar cambios de visibilidad
  useEffect(() => {
    onVisibilityChange?.(actualIsVisible);
  }, [actualIsVisible, onVisibilityChange]);

  // Auto-scroll al final cuando hay nuevos mensajes
  useEffect(() => {
    if (scrollRef.current && actualIsVisible) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, actualIsVisible]);

  // Focus en el input cuando se muestra el chat
  useEffect(() => {
    // Embebido: sin autofocus al montar — en móvil abriría el teclado aunque
    // el estudiante esté mirando la pestaña de episodios.
    if (embedded) return;
    if (actualIsVisible && inputRef.current) {
      inputRef.current.focus();
    }
  }, [actualIsVisible, embedded]);

  useLayoutEffect(() => {
    if (!inline || !actualIsVisible) return;
    const rafId = requestAnimationFrame(() => {
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const portrait = window.matchMedia('(orientation: portrait)').matches;
      const feedbackExpanded = !!document.querySelector('.feedback-banner.expanded');
      const rect = panelRef.current?.getBoundingClientRect();
    });
    return () => cancelAnimationFrame(rafId);
  }, [inline, actualIsVisible]);

  const handleToggle = () => {
    const newVisibility = !isVisible;
    setIsVisible(newVisibility);
    onVisibilityChange?.(newVisibility);
  };

  // Ajusta la altura del textarea a su contenido (multilínea), con tope.
  const autoGrow = (el: HTMLTextAreaElement | null) => {
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 140)}px`;
  };

  // Reajustar altura cuando cambia el texto (incluye al limpiarlo tras enviar).
  useLayoutEffect(() => {
    autoGrow(inputRef.current);
  }, [inputText, actualIsVisible]);

  const submitMessage = () => {
    // Guard para evitar envíos duplicados
    if (isSendingRef.current) {
      return;
    }

    const textToSend = inputText.trim();

    // Permitir enviar incluso si está vacío - sendTextMessage manejará el caso
    if (onSendMessage) {
      isSendingRef.current = true; // Marcar como enviando
      setInputText(''); // Limpiar input primero
      onSendMessage(textToSend);

      // Resetear flag después de un breve delay para permitir el siguiente envío
      setTimeout(() => {
        isSendingRef.current = false;
      }, 500);
    }
  };

  const handleSend = (e: React.FormEvent) => {
    e.preventDefault();
    submitMessage();
  };

  // Enter envía; Shift+Enter inserta un salto de párrafo.
  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submitMessage();
    }
  };

  // Cuerpo del panel (pestañas, mensajes, sugerencias, documento y campo de
  // texto): lo comparten el modo flotante `inline` y el modo `embedded`.
  const panelBody = (
    <>
            {renderTabs()}
            <div className="chat-messages" ref={scrollRef}>
              {messages.length === 0 ? (
                <div className="chat-empty">
                  <p>{t.empty}</p>
                </div>
              ) : (
                messages.map((message, index) => (
                  <ChatMessageComponent
                    key={index}
                    message={message}
                    onClick={handleMessageClick}
                    onCopy={handleCopyMessage}
                  />
                ))
              )}
            </div>
            {/* Chips de sugerencias: solo en modo profesor. En modo estudiante los
                reemplazan las pestañas de conversaciones recientes (Santiago 01/09). */}
            {messages.length === 0 && isAideProf && (
              <div className="chat-suggestions">
                {suggestions.map((suggestion) => (
                  <button
                    key={suggestion}
                    className="chat-suggestion-pill"
                    onClick={() => {
                      if (onSendMessage) {
                        onSendMessage(suggestion);
                      }
                    }}
                    type="button"
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            )}
            {showAttach && (docStatus !== 'idle' || attachedDocName) && (
              <div
                className="chat-doc-indicator"
                style={{
                  padding: '2px 8px 6px',
                  fontSize: 12,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  color: docStatus === 'error' ? '#c0392b' : '#0a7d6b',
                }}
              >
                {docStatus === 'loading'
                  ? <span>{t.docProcessing}</span>
                  : docStatus === 'error'
                    ? <span>⚠ {docError}</span>
                    : <span>📎 {attachedDocName} {t.docAttached}</span>}
              </div>
            )}
            <form className="chat-input-form" onSubmit={handleSend}>
              {showAttach && (
                <>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,.txt"
                    style={{ display: 'none' }}
                    onChange={handleFileChange}
                  />
                  <button
                    type="button"
                    className="chat-attach-button"
                    onClick={handleAttachClick}
                    disabled={docStatus === 'loading'}
                    aria-label={t.attach}
                    title={t.attach}
                    style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 6, color: '#ff6b35', display: 'flex', alignItems: 'center' }}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/>
                    </svg>
                  </button>
                </>
              )}
              <textarea
                ref={inputRef}
                rows={1}
                className="chat-input"
                placeholder={placeholder ?? t.placeholder}
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                onPaste={(e) => handlePaste(e, setInputText)}
                onKeyDown={handleInputKeyDown}
              />
              {inputAccessory}
              <button type="submit" className="chat-send-button" aria-label={t.send}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="send-icon-svg">
                  <line x1="22" y1="2" x2="11" y2="13"/>
                  <polygon points="22 2 15 22 11 13 2 9 22 2"/>
                </svg>
              </button>
            </form>
    </>
  );

  if (embedded) {
    return <div className="chat-panel-embedded">{panelBody}</div>;
  }

  if (inline) {
    // Modo inline: botón en la barra de controles y panel flotante
    return (
      <>
        <button
          className="chat-toggle-button-inline"
          onClick={handleToggle}
          aria-label={actualIsVisible ? t.hideChat : t.showChat}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>
          </svg>
        </button>
        {actualIsVisible && createPortal(
          <div className="chat-panel-inline" ref={panelRef}>
            {panelBody}
          </div>,
          document.body
        )}
      </>
    );
  }

  return (
    <div className={`chat-container ${actualIsVisible ? 'visible' : ''}`}>
      <button
        className="chat-toggle-button"
        onClick={handleToggle}
        aria-label={actualIsVisible ? t.hideChat : t.showChat}
      >
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="var(--brand-orange-light, #ff6b35)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="chat-icon-svg">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
        </svg>
      </button>

      {actualIsVisible && (
        <div className="chat-panel">
          {renderTabs()}
          <div className="chat-messages" ref={scrollRef}>
            {messages.length === 0 ? (
              <div className="chat-empty">
                <p>{t.empty}</p>
              </div>
            ) : (
              messages.map((message, index) => (
                <ChatMessageComponent
                  key={index}
                  message={message}
                  onClick={handleMessageClick}
                  onCopy={handleCopyMessage}
                />
              ))
            )}
          </div>
          
          <form className="chat-input-form" onSubmit={handleSend}>
            <textarea
              ref={inputRef}
              rows={1}
              className="chat-input"
              placeholder={t.placeholder}
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              onPaste={(e) => handlePaste(e, setInputText)}
              onKeyDown={handleInputKeyDown}
            />
            <button type="submit" className="chat-send-button" aria-label={t.send}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="send-icon-svg">
                <line x1="22" y1="2" x2="11" y2="13"/>
                <polygon points="22 2 15 22 11 13 2 9 22 2"/>
              </svg>
            </button>
          </form>
        </div>
      )}
    </div>
  );
}


